import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "../components/Card";
import { Button } from "../components/Button";
import { TextInput } from "../components/TextInput";
import { OtpInput } from "../components/OtpInput";
import { ErrorBanner } from "../components/ErrorBanner";
import { CenteredSpinner } from "../components/CenteredSpinner";
import { supabase } from "../lib/supabase";
import { describeAuthError, isValidEmail } from "../lib/auth-errors";
import { strings } from "../lib/strings";
import { APP_URL, OTP_LENGTH, OTP_RESEND_COOLDOWN_SECONDS } from "../lib/env";
import { useResendCooldown } from "../hooks/useResendCooldown";

type Stage = "email" | "otp" | "confirm" | "deleting" | "success" | "fatal";

interface PageState {
  stage: Stage;
  email: string;
  code: string;
  acknowledged: boolean;
  error: string | null;
  otpError: string | null;
  isSendingOtp: boolean;
  isVerifyingOtp: boolean;
  fatalTitle: string | null;
  fatalBody: string | null;
}

const initialState: PageState = {
  stage: "email",
  email: "",
  code: "",
  acknowledged: false,
  error: null,
  otpError: null,
  isSendingOtp: false,
  isVerifyingOtp: false,
  fatalTitle: null,
  fatalBody: null,
};

export function DeleteAccountPage(): JSX.Element {
  const [params] = useSearchParams();
  const [state, setState] = useState<PageState>(initialState);
  const cooldown = useResendCooldown(OTP_RESEND_COOLDOWN_SECONDS);

  // When the user opens the page from the magic link in the email, GoTrue
  // includes a `token_hash` plus a `type` (typically `magiclink`, sometimes
  // `email`) that we pass straight to verifyOtp. This avoids ever asking for
  // the 6-digit code when the user already proved control of the inbox.
  const tokenHashFromLink: string | null = useMemo(
    () => params.get("token_hash"),
    [params],
  );
  const linkType: "magiclink" | "email" = useMemo(() => {
    const raw: string | null = params.get("type");
    return raw === "email" ? "email" : "magiclink";
  }, [params]);

  useEffect(() => {
    if (!tokenHashFromLink) return;

    let cancelled = false;
    const verifyFromLink = async (): Promise<void> => {
      const { error } = await supabase.auth.verifyOtp({
        token_hash: tokenHashFromLink,
        type: linkType,
      });
      if (cancelled) return;
      if (error) {
        setState((prev) => ({
          ...prev,
          stage: "fatal",
          fatalTitle: strings.delete.reverificationTitle,
          fatalBody: strings.delete.reverificationBody,
          error: describeAuthError(error),
        }));
        return;
      }

      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      const verifiedEmail: string = userData.user?.email ?? "";
      setState((prev) => ({
        ...prev,
        stage: "confirm",
        email: verifiedEmail,
        error: null,
      }));
    };

    void verifyFromLink();
    return () => {
      cancelled = true;
    };
  }, [tokenHashFromLink, linkType]);

  const sendOtp = async (): Promise<void> => {
    const trimmedEmail: string = state.email.trim();
    if (!isValidEmail(trimmedEmail)) {
      setState((prev) => ({ ...prev, error: strings.common.invalidEmail }));
      return;
    }
    setState((prev) => ({
      ...prev,
      isSendingOtp: true,
      error: null,
      email: trimmedEmail,
    }));
    const { error } = await supabase.auth.signInWithOtp({
      email: trimmedEmail,
      options: { shouldCreateUser: false },
    });
    if (error) {
      setState((prev) => ({
        ...prev,
        isSendingOtp: false,
        error: describeAuthError(error),
      }));
      return;
    }
    cooldown.start();
    setState((prev) => ({
      ...prev,
      isSendingOtp: false,
      stage: "otp",
      code: "",
      otpError: null,
    }));
  };

  const verifyOtp = async (): Promise<void> => {
    if (state.code.length !== OTP_LENGTH) {
      setState((prev) => ({ ...prev, otpError: strings.common.invalidOtp }));
      return;
    }
    setState((prev) => ({ ...prev, isVerifyingOtp: true, otpError: null }));
    const { error } = await supabase.auth.verifyOtp({
      email: state.email,
      token: state.code,
      type: "email",
    });
    if (error) {
      setState((prev) => ({
        ...prev,
        isVerifyingOtp: false,
        otpError: describeAuthError(error),
      }));
      return;
    }
    setState((prev) => ({ ...prev, isVerifyingOtp: false, stage: "confirm" }));
  };

  const performDelete = async (): Promise<void> => {
    setState((prev) => ({ ...prev, stage: "deleting", error: null }));
    const { error } = await supabase.functions.invoke("delete-account", {
      method: "POST",
    });
    if (error) {
      setState((prev) => ({
        ...prev,
        stage: "confirm",
        error: describeAuthError(error),
      }));
      return;
    }
    await supabase.auth.signOut();
    setState((prev) => ({ ...prev, stage: "success" }));
  };

  if (tokenHashFromLink && state.stage === "email") {
    return (
      <Card title={strings.delete.pageTitle} subtitle={strings.delete.leadFromLink}>
        <CenteredSpinner label={strings.common.loading} />
      </Card>
    );
  }

  if (state.stage === "fatal") {
    return (
      <Card
        title={state.fatalTitle ?? strings.delete.failureTitle}
        subtitle={state.fatalBody ?? strings.delete.failureBody}
      >
        <ErrorBanner message={state.error} />
        <Button onClick={() => window.location.replace("/delete-account")}>
          {strings.common.retry}
        </Button>
      </Card>
    );
  }

  if (state.stage === "success") {
    return (
      <Card>
        <div className="success">
          <span className="success__icon" aria-hidden="true">
            ✓
          </span>
          <h1 className="success__title">{strings.delete.successTitle}</h1>
          <p className="success__body">{strings.delete.successBody}</p>
          <a
            href={`${APP_URL}/account-deleted`}
            className="button button--primary success__cta"
          >
            <span className="button__label">{strings.common.openApp}</span>
          </a>
        </div>
      </Card>
    );
  }

  if (state.stage === "deleting") {
    return (
      <Card title={strings.delete.pageTitle}>
        <CenteredSpinner label={strings.common.loading} />
      </Card>
    );
  }

  if (state.stage === "confirm") {
    return (
      <Card
        title={strings.delete.consequencesTitle}
        subtitle={state.email ? `Account: ${state.email}` : undefined}
      >
        <ul className="consequences">
          {strings.delete.consequences.map((line) => (
            <li key={line}>{line}</li>
          ))}
        </ul>
        <label className="checkbox">
          <input
            type="checkbox"
            checked={state.acknowledged}
            onChange={(e) =>
              setState((prev) => ({ ...prev, acknowledged: e.target.checked }))
            }
          />
          <span>{strings.delete.consequencesAck}</span>
        </label>
        <ErrorBanner message={state.error} />
        <Button
          variant="danger"
          disabled={!state.acknowledged}
          onClick={() => void performDelete()}
        >
          {strings.delete.confirmDelete}
        </Button>
      </Card>
    );
  }

  if (state.stage === "otp") {
    const resendLabel: string = cooldown.isCoolingDown
      ? strings.common.otpResendCountdown(cooldown.remaining)
      : strings.common.otpResend;
    return (
      <Card
        title={strings.delete.pageTitle}
        subtitle={strings.delete.enterCodeTitle.replace("{email}", state.email)}
      >
        <OtpInput
          label={strings.common.otpLabel}
          placeholder={strings.common.otpPlaceholder}
          value={state.code}
          onChange={(next) =>
            setState((prev) => ({ ...prev, code: next, otpError: null }))
          }
          autoFocus
          error={state.otpError}
          disabled={state.isVerifyingOtp}
        />
        <Button
          loading={state.isVerifyingOtp}
          disabled={state.code.length !== OTP_LENGTH}
          onClick={() => void verifyOtp()}
        >
          {strings.common.continue}
        </Button>
        <Button
          variant="link"
          disabled={cooldown.isCoolingDown || state.isSendingOtp}
          onClick={() => void sendOtp()}
        >
          {resendLabel}
        </Button>
      </Card>
    );
  }

  return (
    <Card title={strings.delete.pageTitle} subtitle={strings.delete.leadStandalone}>
      <TextInput
        type="email"
        label={strings.common.emailLabel}
        placeholder={strings.common.emailPlaceholder}
        autoComplete="email"
        autoFocus
        value={state.email}
        error={state.error}
        onChange={(e) =>
          setState((prev) => ({ ...prev, email: e.target.value, error: null }))
        }
      />
      <Button
        loading={state.isSendingOtp}
        disabled={!state.email.trim() || state.isSendingOtp}
        onClick={() => void sendOtp()}
      >
        {strings.delete.sendCode}
      </Button>
    </Card>
  );
}
